import React, { useEffect, useRef, useState } from "react";
import {
  getTrackingEvent,
  getCustomVoucher,
  postTrackingBundle,
  postOfferView,
} from "../../../../services/offers/offers.service";
import {
  CampaignType,
  ICONS_DEFAULT_VALUES,
  LayoutOptions,
  Offer,
  OffersIdSlots,
  OtherSettingsInterface,
  PlacementStage,
  QueryBundle,
  QueryPostOfferView,
  RedeemBy,
  Template,
  TemplateIcons,
  TemplateOptionSettings,
  VoucherType,
} from "../../../../types/global.types";
import PreSelect from "../../components/PreSelect/PreSelect";
import TemplateIcon from "../../components/Icons/info.component";
import BlockedTag from "../../components/BlockedTag/blockedTag.component";
import ImageCarousel from "../../../ImageCarousel/ImageCarousel.component";
interface Props {
  offer: Offer;
  eventOfferIds: OffersIdSlots[];
  settings: TemplateOptionSettings;
  settingsTemplate: Template;
  displayOnly: boolean;
  retailerId: string;
  campaignId: string;
  placementId: string;
  placementName: string;
  campaign: CampaignType;
  slot: number;
  isAdded: boolean;
  sessionId: string;
  setPreEmail?: (email: string) => void;
  preEmail?: string | undefined;
  openSingle: (
    o: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number,
    isShare?: boolean
  ) => void;
  onAdd: (offer: Offer, value: boolean, slot: number) => void;
  hasVoucher: boolean;
  otherSettings: OtherSettingsInterface | undefined;
  layoutId: string;
  layoutOptions: LayoutOptions | undefined;
  isSeen: boolean;
  canAutoSendEmailFromLabel?: boolean;
  canAutoSendEmailFromMainCTA?: boolean;
  onAutoSendEmailFromLabel?: (offer: Offer, slot: number) => void;
  autoSendingOfferId?: boolean;
  offerCardSize?: "normal" | "large";
  showLogo?: boolean;
}

const OfferItem = ({
  offer,
  eventOfferIds,
  settings,
  campaign,
  settingsTemplate,
  displayOnly,
  retailerId,
  campaignId,
  placementId,
  placementName,
  slot,
  isAdded,
  openSingle,
  onAdd,
  hasVoucher,
  otherSettings,
  sessionId,
  layoutId,
  layoutOptions,
  isSeen,
  canAutoSendEmailFromLabel,
  canAutoSendEmailFromMainCTA,
  onAutoSendEmailFromLabel,
  autoSendingOfferId,
  offerCardSize,
  showLogo,
}: Props) => {
  const [copyCode, setCopyCode] = useState<boolean>(false);
  const [getCode, setGetCode] = useState<boolean>(false);
  const [copied, setCopied] = useState(false);
  const [customVoucher, setCustomVoucher] = useState<string>("");
  const [voucherLoading, setVoucherLoading] = useState<boolean>(false);
  const elmRef = useRef<HTMLDivElement>(null);

  function observeIframeVisibility(elm: HTMLElement, callback: () => void) {
    if (!elm) {
      console.error(`Element not found.`);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            callback();
            // Optionally, stop observing after the first intersection
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null, // Use the viewport as the root
        rootMargin: "0px",
        threshold: 0.5, // Trigger when at least 50% of the iframe is visible
      }
    );

    observer.observe(elm);
  }

  const setDefaultOfferImpressionEvent = async () => {
    if (offer) {
      const data: QueryPostOfferView = {
        offers: [{ id: offer.id, slot }],
        campaignId,
        campaignName: campaign.name || "",
        companyId: campaign.company_Id,
        placementId,
        placementName,
        pageUrl: "",
        templateId: settingsTemplate.identifier,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        layoutId,
      };

      await postOfferView(data);
    }
  };

  useEffect(() => {
    if (!isSeen || !elmRef.current) {
      return;
    }

    observeIframeVisibility(elmRef.current, () => {
      setDefaultOfferImpressionEvent();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSeen]);

  const getOfferSlotById = (
    offerId: string,
    offerIdAndSlots: OffersIdSlots[]
  ): number => offerIdAndSlots.find((f) => f.id === offerId)?.slot || 0;

  const handleAdd = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLElement>
      | React.MouseEvent<HTMLElement>,
    offer: Offer,
    value: boolean
  ) => {
    const slot = getOfferSlotById(offer.id, eventOfferIds);
    if (displayOnly && !offer.tagBlocked.isBlocked) {
      onAdd(offer, value, slot);
      value && handleOpenEmailInput(offer);
    }
  };

  const handleOpenEmailInput = (offer: Offer) => {
    if (
      campaign.campaignGeneralOptions?.showAddToOrder &&
      campaign.campaignGeneralOptions?.emailCaptureOnOffers &&
      campaign.campaignGeneralOptions?.emailCaptureOnAddToOrder
    ) {
      const slot = getOfferSlotById(offer.id, eventOfferIds) || 0;
      openSingle(offer, false, true, slot);
    }
  };

  const handleGetGift = async (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    url: string,
    slot: number,
    offer: Offer
  ) => {
    if (offer?.voucher?.voucherType === VoucherType.CUSTOM && !getCode) {
      return;
    }

    if (
      event.target instanceof HTMLElement &&
      !event.target.classList.contains("item-info-icon") &&
      !event.target.classList.contains("item-share-icon") &&
      !event.target.classList.contains("item-email-icon") &&
      !event.target.classList.contains("copy-box") &&
      !event.target.classList.contains("email-target") &&
      !displayOnly &&
      offer.offerSettings.redeemBy === RedeemBy.webemail
    ) {
      if (offer.offerSettings.bundleOptions.isBundled) {
        const data: QueryBundle = {
          offerId: offer.id,
          campaignId: campaign.id,
          placementId: placementId,
          orderId: offer.orderId,
          userSessionId: sessionId,
          imageUrl: offer.imageUrl,
          pageUrl: window.location.href,
          templateId: settingsTemplate.identifier,
          placementName: placementName,
          pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
          slot: slot,
          layoutId: layoutId,
        };

        const response = await postTrackingBundle(data);

        if (response?.data?.status) {
          window.open(`/bundle-offer/${response?.data?.result}`, "_blank");
        }
      } else {
        window.open(
          url + `&tid=${settingsTemplate.identifier}&slot=${slot}`,
          "_blank"
        );
      }
    }
  };

  const handleClickInfo = (
    event: React.MouseEvent | React.KeyboardEvent,
    offer: Offer
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const slot = getOfferSlotById(offer.id, eventOfferIds);
    openSingle(offer, false, false, slot);
    getTrackingEvent({
      event: "onreadmoreclick",
      offerId: offer.id,
      retailerId,
      campaignId,
      placementId,
      placementName,
      tid: settingsTemplate.identifier,
      slot,
      pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
      pltid: layoutId,
    });
  };

  const handleClickShare = (
    event: React.MouseEvent | React.KeyboardEvent,
    offer: Offer
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const slot = getOfferSlotById(offer.id, eventOfferIds);
    openSingle(offer, false, false, slot, true);
  };

  const handleSaveEmail = (
    event: React.MouseEvent | React.KeyboardEvent,
    offer: Offer
  ) => {
    event.preventDefault();
    const slot = getOfferSlotById(offer.id, eventOfferIds);
    openSingle(offer, true, false, slot);
  };

  const handleCopyHtml = () => {
    window.focus();
    navigator.clipboard?.writeText(
      customVoucher ? customVoucher : offer.voucher?.code
    );
    setCopied(true);
    setCopyCode(false);
    setTimeout(() => {
      setCopied(false);
    }, 1000); // Show the message for 1 second
  };

  const handleGetCode = async () => {
    setVoucherLoading(true);
    const customVoucher = await getCustomVoucher({
      offerId: offer.id,
      campaignId: campaign.id,
      campaignName: campaign.name,
      companyId: campaign.company_Id,
      placementId: placementId,
      placementName: placementName,
      templateId: settingsTemplate.identifier,
      layoutId: layoutId,
      orderId: offer.orderId,
    });
    setCustomVoucher(customVoucher?.data?.result);
    setGetCode(true);
    setVoucherLoading(false);
  };

  const handleMainCtaAutoSend = () => {
    if (
      canAutoSendEmailFromMainCTA &&
      onAutoSendEmailFromLabel &&
      offer.offerSettings.redeemBy === RedeemBy.webemail &&
      !offer.offerSettings?.bundleOptions?.isBundled &&
      !autoSendingOfferId
    ) {
      onAutoSendEmailFromLabel(offer, slot);
    }
  };

  const handleEmailLabelClick = (
    event: React.MouseEvent | React.KeyboardEvent
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      canAutoSendEmailFromLabel &&
      onAutoSendEmailFromLabel &&
      offer.offerSettings.redeemBy === RedeemBy.webemail
    ) {
      onAutoSendEmailFromLabel(offer, slot);
      return;
    }

    handleSaveEmail(event, offer);
  };

  const getVoucherText = () => {
    return customVoucher ? (
      <>
        <span style={{ fontWeight: "400", marginRight: "3px" }}>Code: </span>

        <span style={{ fontWeight: "600" }}>{customVoucher}</span>
      </>
    ) : offer.voucher?.code ? (
      <>
        <span style={{ fontWeight: "400", marginRight: "3px" }}>Code: </span>
        <span style={{ fontWeight: "600" }}>{offer.voucher?.code}</span>
      </>
    ) : (
      <span style={{ fontWeight: "400" }}>Sorry, code just expired!</span>
    );
  };

  const showEmailLabel =
    !displayOnly &&
    campaign.campaignGeneralOptions.saveOfferOptions.allowSavingOffers &&
    campaign.campaignGeneralOptions.saveOfferOptions.allowEmail &&
    (layoutOptions?.showEmail ?? true) &&
    !offer.offerSettings?.bundleOptions?.isBundled;
  const isLargeCard = offerCardSize === "large";
  const showOfferLogo = isLargeCard && showLogo !== false;
  const featuredImages = offer.media?.featured?.images || [];
  const mainImages = offer.media?.mainImage || [];
  const cardImages = (
    isLargeCard && featuredImages.length > 0 ? featuredImages : mainImages
  ).map((image) => image.filepath);
  const imageUrl =
    cardImages[0] || offer.imageUrl;
  const advertiserLogoUrl = offer.advertiserLogo;

  return (
    <div
      key={`compact-${offer.id}`}
      ref={elmRef}
      className={`grid-item  grid-${settings?.dealLayout?.align} ${displayOnly ? "display" : "getbutton"
        }`}
    >
      <input
        className="input"
        id={offer.id}
        type="checkbox"
        disabled={
          !displayOnly || !campaign.campaignGeneralOptions?.showAddToOrder
        }
        checked={isAdded}
        onChange={(e) => handleAdd(e, offer, !isAdded)}
      />
      <label
        htmlFor={offer.id}
        className="item-layout"
        tabIndex={0}
        onClick={(e) =>
          handleGetGift(
            e,
            offer.trackingUrl,
            getOfferSlotById(offer.id, eventOfferIds),
            offer
          )
        }
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.stopPropagation();
            displayOnly
              ? handleAdd(e, offer, !isAdded)
              : handleGetGift(
                e,
                offer.trackingUrl,
                getOfferSlotById(offer.id, eventOfferIds),
                offer
              );
          }
        }}
      >
        {(layoutOptions?.showBadge ?? true) && (
          <span
            style={{
              position: "absolute",
              top: "-8.5px",
              left: "20px",
              fontSize: "10px",
              textTransform: "uppercase",
              color: offer.badge?.fontColor,
              backgroundColor: offer.badge?.backgroundColor,
              padding: "0 6px",
              borderRadius: "3px",
              fontWeight: "600",
              zIndex: 10,
            }}
            className="badge-settings"
          >
            {offer.badge?.name}
          </span>
        )}
        <div
          className={`item-layout-inner${showOfferLogo ? " offer-card-size-large" : ""}`}
          style={{
            borderRadius: `${settings?.mainLayout?.borderRadius}px`,
            border:
              offer.badge?.useBorder && (layoutOptions?.showBadge ?? true)
                ? `1px solid ${isAdded
                  ? settings.mainLayout?.highlightColor ||
                  settings.dealLayout.borderColor
                  : offer.badge?.backgroundColor
                }`
                : `1px solid ${displayOnly
                  ? isAdded
                    ? settings.mainLayout?.highlightColor ||
                    settings?.dealLayout.borderColor
                    : settings?.dealLayout.borderColor
                  : settings?.dealLayout.borderColor
                }`,
            backgroundColor: settings?.dealLayout?.backgroundColor,
            fontSize: settings?.dealLayout?.fontSize,
            color: settings?.dealLayout?.textColor,
            position: "relative",
          }}
        >
          {offer.tagBlocked.isBlocked && (
            <BlockedTag tagBlocked={offer.tagBlocked} />
          )}
          {/* <div
          className="border"
          style={{ borderColor: settings?.mainLayout?.highlightColor }}
        ></div> */}
          <div className={`item-image${isLargeCard ? " is-large" : ""}`}>
            {cardImages.length > 0 ? (
              <ImageCarousel
                images={cardImages}
                showPagination={true}
                borderRadius={0}
                altPrefix="offer"
                style={{ width: "100%", height: "auto" }}
              />
            ) : (
              <img
                className="image-large"
                src={imageUrl}
                alt={offer.title}
                role="presentation"
              />
            )}
            {showOfferLogo && advertiserLogoUrl && (
              <div className="item-advertiser-logo" style={{ borderRadius: `${settings?.mainLayout?.borderRadius}px` }}>
                <img src={advertiserLogoUrl} alt="" />
              </div>
            )}
          </div>
          <div
            className={`item-content`}
            style={{
              marginBottom:
                offer.voucher?.voucherType && !showEmailLabel ? "20px" : undefined,
            }}
          >
            <div className="item-title">
              <span
                className={`item-title-text max-2lines `}
                style={{
                  color: settings?.dealLayout?.textColor,
                  fontSize: `${Number(settings?.dealLayout?.fontSize) + 1}px`,
                }}
                tabIndex={0}
                role="contentinfo"
                aria-label={offer.title}
              >
                {offer.title}
              </span>
            </div>

            <div
              className="item-description max-2lines"
              style={{
                color: settings?.dealLayout.textColor,
                fontSize: `${settings?.dealLayout.fontSize}px`,
              }}
            >
              <span
                tabIndex={0}
                role="contentinfo"
                aria-label={offer.description}
              >
                {offer.description}
              </span>
            </div>

            <div className="item-get">
              <div className="item-get-center">
                {displayOnly ? (
                  campaign.campaignGeneralOptions.showAddToOrder && (
                    <PreSelect
                      offerId={offer.id}
                      buttonFontWeight={settings?.dealLayout.buttonFontWeight}
                      buttonFontSize={settings?.dealLayout.buttonFontSize}
                      showCheckboxSelect={otherSettings?.showCheckboxSelect}
                      checked={isAdded}
                      highlightColor={settings?.mainLayout?.highlightColor}
                      borderColor={settings?.dealLayout?.borderColor}
                      selectedButtonText={otherSettings?.selectedButtonText}
                      notSelectedButtonText={otherSettings?.notSelectedButtonText}
                      textColor={otherSettings?.textColor}
                      settings={otherSettings}
                    />
                  )
                ) : (
                  <>
                    {offer.offerSettings.redeemBy === RedeemBy.email ? (
                      offer?.voucher?.voucherType === VoucherType.CUSTOM &&
                        !getCode ? (
                        <div
                          className="item-action-get dashed-button-super copy-box"
                          onClick={handleGetCode}
                          tabIndex={0}
                          role="button"
                          aria-label="Get code"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.stopPropagation();
                              handleGetCode();
                            }
                          }}
                          style={{
                            fontSize: `${settings?.dealLayout?.buttonFontSize}px`,
                          }}
                        >
                          {voucherLoading ? (
                            <svg
                              className="spin"
                              viewBox="0 0 1024 1024"
                              focusable="false"
                              data-icon="loading"
                              width="1em"
                              height="1em"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z"></path>
                            </svg>
                          ) : (
                            "Get code"
                          )}
                        </div>
                      ) : (
                        <div
                          className="item-action-get item-action-email"
                          onClick={(e) => handleSaveEmail(e, offer)}
                          tabIndex={0}
                          role="button"
                          aria-label="Save offer to email"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.stopPropagation();
                              handleSaveEmail(e, offer);
                            }
                          }}
                          style={{
                            backgroundColor:
                              settings?.dealLayout?.emailButtonBackgroundColor,
                            color: settings?.dealLayout?.emailButtonTextColor,
                            marginRight: "0",
                            fontSize: "14px",
                            border: `1px solid transparent`,
                            borderRadius: `${settings?.dealLayout?.buttonRounding}px`,
                          }}
                        >
                          <TemplateIcon
                            icon={
                              settings?.dealLayout.iconEmail?.value ||
                              ICONS_DEFAULT_VALUES.iconEmail
                            }
                            color={settings?.dealLayout?.emailButtonTextColor}
                          />
                          {offer.offerCTAText}
                        </div>
                      )
                    ) : offer?.voucher?.voucherType === VoucherType.CUSTOM &&
                      !getCode ? (
                      <div
                        className="item-action-get dashed-button-super copy-box"
                        onClick={handleGetCode}
                        tabIndex={0}
                        role="button"
                        aria-label="Get code"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.stopPropagation();
                            handleGetCode();
                          }
                        }}
                        style={{
                          fontSize: `${settings?.dealLayout?.buttonFontSize}px`,
                        }}
                      >
                        {voucherLoading ? (
                          <svg
                            className="spin"
                            viewBox="0 0 1024 1024"
                            focusable="false"
                            data-icon="loading"
                            width="1em"
                            height="1em"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z"></path>
                          </svg>
                        ) : (
                          "Get code"
                        )}
                      </div>
                    ) : (
                      <div
                        className="item-action-get"
                        tabIndex={0}
                        role="button"
                        aria-label="Get offer now"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleMainCtaAutoSend();
                          handleGetGift(
                            e,
                            offer.trackingUrl,
                            getOfferSlotById(offer.id, eventOfferIds),
                            offer
                          );
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.stopPropagation();
                            handleMainCtaAutoSend();
                            handleGetGift(
                              e,
                              offer.trackingUrl,
                              getOfferSlotById(offer.id, eventOfferIds),
                              offer
                            );
                          }
                        }}
                        style={{
                          color: settings?.dealLayout?.buttonTextColor,
                          fontSize: `${settings?.dealLayout?.buttonFontSize}px`,
                          fontWeight: settings?.dealLayout?.buttonFontWeight,
                          backgroundColor:
                            settings?.dealLayout?.buttonBackgroundColor,
                          borderRadius: `${settings?.dealLayout?.buttonRounding}px`,
                          border: `1px solid ${settings?.dealLayout?.buttonBorderColor ||
                            settings?.dealLayout?.buttonBackgroundColor
                            }`,
                          textTransform: settings?.dealLayout.buttonTextTransform,
                        }}
                      >
                        {offer.offerCTAText || "Get offer now"}
                      </div>
                    )}
                  </>
                )}

                <div className="item-icons">
                  <div
                    className="item-info-icon"
                    onClick={(e) => handleClickInfo(e, offer)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.stopPropagation();
                        handleClickInfo(e, offer);
                      }
                    }}
                    role="button"
                    aria-label={`More information about ${offer.title}`}
                  >
                    {!settings?.dealLayout?.icons ||
                      settings?.dealLayout?.icons === TemplateIcons.default ? (
                      <TemplateIcon
                        icon={
                          settings?.dealLayout.iconInfo?.value ||
                          ICONS_DEFAULT_VALUES.iconInfo
                        }
                        color={settings?.dealLayout.textColor}
                      />
                    ) : (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12 0C5.37 0 0 5.37 0 12C0 18.63 5.37 24 12 24C18.63 24 24 18.63 24 12C24 5.37 18.63 0 12 0ZM13.5 18.5C13.5 19.33 12.83 20 12 20C11.17 20 10.5 19.33 10.5 18.5V10.5C10.5 9.67 11.17 9 12 9C12.83 9 13.5 9.67 13.5 10.5V18.5ZM12 7C11.17 7 10.5 6.33 10.5 5.5C10.5 4.67 11.17 4 12 4C12.83 4 13.5 4.67 13.5 5.5C13.5 6.33 12.83 7 12 7Z"
                          fill={settings?.dealLayout.textColor}
                        />
                      </svg>
                    )}
                  </div>
                  {showEmailLabel && (
                    <div
                      className="item-email-icon item-info-icon email-target"
                      onClick={handleEmailLabelClick}
                      tabIndex={0}
                      role="button"
                      aria-label="Save offer to email"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.stopPropagation();
                          handleEmailLabelClick(e);
                        }
                      }}
                    >
                      {!settings?.dealLayout?.icons ||
                        settings?.dealLayout?.icons === TemplateIcons.default ? (
                        <TemplateIcon
                          icon={
                            settings?.dealLayout.iconEmail?.value ||
                            ICONS_DEFAULT_VALUES.iconEmail
                          }
                          color={settings?.dealLayout.textColor || "#888888"}
                        />
                      ) : (
                        <svg
                          width="32"
                          height="24"
                          viewBox="0 0 32 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 4.00024H3C2.45 4.00024 2 4.45024 2 5.00024V19.0002C2 19.5502 2.45 20.0002 3 20.0002H21C21.55 20.0002 22 19.5502 22 19.0002V5.00024C22 4.45024 21.55 4.00024 21 4.00024ZM20.38 5.00024L12 13.0802L3.69 5.00024H20.38ZM3 19.0002V5.72024L12 14.4702L21 5.80024V19.0002H3Z"
                            fill={settings?.dealLayout.textColor || "#888888"}
                          />
                        </svg>
                      )}
                    </div>
                  )}
                  {!displayOnly &&
                    campaign.sharingOptions?.allowSharing &&
                    campaign.sharingOptions.sharingPlatforms?.platforms?.length >
                    0 &&
                    offer?.offerSettings?.allowShare &&
                    (layoutOptions?.showShare ?? true) && (
                      <div
                        className="item-share-icon item-info-icon"
                        onClick={(e) => handleClickShare(e, offer)}
                        tabIndex={0}
                        role="button"
                        aria-label="Share offer"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.stopPropagation();
                            handleClickShare(e, offer);
                          }
                        }}
                      >
                        <TemplateIcon
                          icon={
                            settings?.dealLayout.iconShare?.value ||
                            ICONS_DEFAULT_VALUES.iconShare
                          }
                          color={settings?.dealLayout.textColor}
                        />
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
          {showEmailLabel && (
            <div
              className="item-email-label email-target"
              style={{
                backgroundColor: `${settings?.dealLayout?.emailBackground}`,
                borderTop: `1px solid ${settings?.dealLayout?.emailBorderColor}`,
                marginBottom: offer.voucher?.voucherType ? "20px" : undefined,
              }}
              onClick={handleEmailLabelClick}
            >
              <span
                className="item-email-icon email-target"
                style={{ backgroundColor: settings?.dealLayout?.backgroundColor }}
              >
                {!settings?.dealLayout?.icons ||
                  settings?.dealLayout?.icons === TemplateIcons.default ? (
                  <TemplateIcon
                    icon={
                      settings?.dealLayout.iconEmail?.value ||
                      ICONS_DEFAULT_VALUES.iconEmail
                    }
                    color={settings?.dealLayout?.emailIconColor || "#888888"}
                  />
                ) : (
                  <svg
                    width="32"
                    height="24"
                    viewBox="0 0 32 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginLeft: 4 }}
                  >
                    <path
                      d="M21 4.00024H3C2.45 4.00024 2 4.45024 2 5.00024V19.0002C2 19.5502 2.45 20.0002 3 20.0002H21C21.55 20.0002 22 19.5502 22 19.0002V5.00024C22 4.45024 21.55 4.00024 21 4.00024ZM20.38 5.00024L12 13.0802L3.69 5.00024H20.38ZM3 19.0002V5.72024L12 14.4702L21 5.80024V19.0002H3Z"
                      fill={settings?.dealLayout?.emailIconColor || "#888888"}
                    />
                  </svg>
                )}
              </span>
              <span
                className="email-target"
                style={{
                  color: settings?.dealLayout?.emailTextColor || "#888888",
                  fontSize: `${settings?.dealLayout?.emailFontSize}px`,
                  fontWeight: settings?.dealLayout?.emailFontWeight || "400",
                }}
              >
                {autoSendingOfferId
                  ? "Sending email..."
                  : settings?.dealLayout?.emailMeTheGift || "Email me the gift"}
              </span>
            </div>
          )}
          {offer.voucher?.voucherType && (
            <span
              className="item-voucher-bar"
              style={{
                position: "absolute",
                bottom: "0",
                left: "0",
                fontSize: "11px",
                color: settings?.dealLayout?.buttonTextColor,
                backgroundColor: settings?.dealLayout?.buttonBackgroundColor,
                //borderRadius: `0 0 ${settings?.mainLayout?.borderRadius || 0}px ${settings?.mainLayout?.borderRadius || 0}px`,
                fontWeight: "600",
                textAlign: "center",
                width: "100%",
                height: "20px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              onMouseEnter={() => setCopyCode(true)}
              onMouseLeave={() => {
                setCopyCode(false);
              }}
              onClick={() => handleCopyHtml()}
              tabIndex={0}
              role="button"
              aria-label="Copy code"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  handleCopyHtml();
                }
              }}
            >
              {offer.voucher?.voucherType === VoucherType.CUSTOM && !getCode ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                  }}
                  className="copy-box"
                  role="button"
                  aria-label="Copy code"
                >
                  Code:
                  <svg
                    style={{
                      marginLeft: "5px",
                    }}
                    width="70"
                    height="16"
                    viewBox="0 0 70 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="70" height="16" fill="" />
                    <circle
                      cx="4"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="10"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="52"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="58"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="16"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="22"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="28"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="34"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="40"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="46"
                      cy="8"
                      r="2"
                      fill={settings?.dealLayout?.buttonTextColor || "white"}
                    />
                  </svg>
                </div>
              ) : (
                <>
                  {copyCode ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                        fontWeight: "400",
                      }}
                      onClick={handleCopyHtml}
                      className="copy-box"
                      role="button"
                      aria-label="Copy code"
                    >
                      {copied ? (
                        <>
                          <svg
                            width="8"
                            height="6"
                            viewBox="0 0 8 6"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{
                              marginRight: "5px",
                            }}
                          >
                            <path
                              d="M1 3L3 5L7 1"
                              stroke={
                                settings?.dealLayout?.buttonTextColor || "white"
                              }
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Copied successfully
                        </>
                      ) : (
                        <>
                          <svg
                            width="11"
                            height="10"
                            viewBox="0 0 11 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{
                              marginRight: "5px",
                            }}
                          >
                            <path
                              d="M7.75 3V2C7.75 1.73478 7.63147 1.48043 7.42049 1.29289C7.20952 1.10536 6.92337 1 6.625 1H2.125C1.82663 1 1.54048 1.10536 1.3295 1.29289C1.11853 1.48043 1 1.73478 1 2V6C1 6.26522 1.11853 6.51957 1.3295 6.70711C1.54048 6.89464 1.82663 7 2.125 7H3.25M4.375 3H8.875C9.49632 3 10 3.44772 10 4V8C10 8.55228 9.49632 9 8.875 9H4.375C3.75368 9 3.25 8.55228 3.25 8V4C3.25 3.44772 3.75368 3 4.375 3Z"
                              stroke={
                                settings?.dealLayout?.buttonTextColor || "white"
                              }
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Copy Code
                        </>
                      )}
                    </div>
                  ) : copied ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        fontWeight: "400",
                      }}
                      className="copy-box"
                    >
                      <svg
                        width="8"
                        height="6"
                        viewBox="0 0 8 6"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          marginRight: "5px",
                        }}
                      >
                        <path
                          d="M1 3L3 5L7 1"
                          stroke={
                            settings?.dealLayout?.buttonTextColor || "white"
                          }
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Copied successfully
                    </div>
                  ) : (
                    getVoucherText()
                  )}
                </>
              )}
            </span>
          )}

        </div>
      </label>
    </div>
  );
};

export default OfferItem;
